import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { IHttpClient } from 'utils/http/httpClient.model';
import { z } from 'zod';

const TimePoint = z.object({
  index: z.number(),
  value: z.number(),
});
const PeriodPoint = z.object({
  index: z.string(),
  value: z.number(),
});
const TimeSerie = z.array(TimePoint).nullable();
const PeriodSerie = z.array(PeriodPoint).nullable();

const Statistics = z.record(z.string(), z.array(z.nullable(z.number()).optional()));
export type Statistics = z.infer<typeof Statistics>;
const BackTestResult = z.object({
  id: z.string(),
  label: z.string().nullable(),
  internalRateReturns: TimeSerie,
  cumulatedAutocallProbabilities: PeriodSerie,
  status: z.string().nullable(),
  message: z.string().nullable(),
});

const BackTestResults = z.array(BackTestResult);
const GetBackTestResponse = z.object({
  results: BackTestResults,
  statistics: Statistics,
});
export type BackTestResults = z.infer<typeof BackTestResults>;
export type GetBackTestResponse = z.infer<typeof GetBackTestResponse>;

export const RequestBackTestResponse = z.object({
  backTestId: z.string(),
});
export type RequestBackTestResponse = z.infer<typeof RequestBackTestResponse>;

export type BackTestResponseTimeSerie<T> = T extends string
  ? z.infer<typeof PeriodSerie>
  : T extends number
    ? z.infer<typeof TimeSerie>
    : never;

export interface BackTestQuoteInfo {
  id: string;
  label?: string;
}
export interface BackTestRequest {
  quotes: BackTestQuoteInfo[];
  maturities: readonly string[];
}
export interface BackTestApi {
  requestBackTest(request: BackTestRequest): Observable<RequestBackTestResponse>;
  getBackTest(backTestId: string): Observable<GetBackTestResponse>;
  cancelBackTest(backTestId: string): Observable<void>;
}

export function backTestApiConnector(httpClient: IHttpClient): BackTestApi {
  return {
    requestBackTest(request: BackTestRequest) {
      return httpClient
        .postJSON<RequestBackTestResponse>({
          url: `api/v2/backtests`,
          targetHost: 'AdvisoryBridge',
          tryParseErrorPayload: true,
          body: request,
        })
        .pipe(map(({ payload }) => RequestBackTestResponse.parse(payload)));
    },
    getBackTest(backTestId) {
      return httpClient
        .getJSON({
          url: `api/v1/backtests/${backTestId}`,
          targetHost: 'AdvisoryBridge',
          tryParseErrorPayload: true,
        })
        .pipe(map(({ payload }) => GetBackTestResponse.parse(payload)));
    },
    cancelBackTest(backTestId) {
      return httpClient
        .delete({
          url: `api/v1/backtests/${backTestId}`,
          targetHost: 'AdvisoryBridge',
          tryParseErrorPayload: true,
        })
        .pipe(map(({ payload }) => payload));
    },
  };
}
